import React, { useState, useRef, useEffect } from "react";
import api from "../api";
import FileStructure from "./FileStructure";
import "bootstrap/dist/css/bootstrap.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { Modal, Button } from "react-bootstrap";

import Status from "./Status";
import Setup from "./Setup";
import Namelists from "./Namelists";
import Output from "./Output";
import Plots from "./Plots";
import BrowsePublished from "./BrowsePublished";

function HomePage({ onLogout }) {
  const [activeDiv, setActiveDiv] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [newJobName, setNewJobName] = useState("");
  const [refreshJobs, setRefreshJobs] = useState(() => () => {});
  const [jobOutputs, setJobOutputs] = useState({});
  const [showBrowse, setShowBrowse] = useState(false);
  const [myEmail, setMyEmail] = useState("");
  const outputRef = useRef(null); // Reference to Output component

  // Current user's email (used for the publish catalog's owner-only actions).
  useEffect(() => {
    api.get("/auth/me")
      .then((res) => setMyEmail(res.data?.user?.email || ""))
      .catch(() => {});
  }, []);

  // Define icon classes, colors, and types for the job control palette
  const palleteIconClasses = [
    "fas fa-plus",
    "fa-solid fa-xmark",
    "fas fa-play",
    "fa-solid fa-pause",
  ];
  const colors = ["#05a4db", "#fa2e00", "#88d500", "#dba405"];
  const types = ["Add Job", "Remove Job", "Run Job", "Pause Job"];

  const handleIconClick = (type) => {
    if (type === "Pause Job") {
      handlePauseJob(); // Directly call pause action without modal
    } else {
      setModalContent(type);
      setShowModal(true);
    }
  };
  const handleButtonClick = (type) => {
    setActiveDiv(type);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSelectJob = async (jobName) => {
    setShowBrowse(false); // selecting a job returns to the job tabs
    if (!jobName) {
      setSelectedJob(null);
      return;
    }
    try {
      const response = await api.get(`/job/${jobName}`);
      setSelectedJob(response.data.job);
    } catch (err) {
      console.error("Error fetching job data:", err);
    }
  };

  const handleAddJob = async () => {
      try {
      const response = await api.post(`/add-job`, {
          job_name: newJobName,
      });
      alert(response.data.message);
      setShowModal(false);
      refreshJobs(); // Refresh the job list after adding a new job
      handleSelectJob(newJobName); // Auto-select the newly created job
    } catch (err) {
      console.error("Error adding job:", err);

      if (err.response && err.response.data && err.response.data.detail) {
        alert(`Error adding job: ${err.response.data.detail}`);
      } else {
        alert("Error adding job");
      }
    }
  };

  const handleDeleteJob = async () => {
    try {
      const response = await api.delete(`/delete-job`, {
        params: { job_name: selectedJob.name },
      });
      alert(response.data.message);
      setShowModal(false);
      refreshJobs(); // Refresh the job list after deleting the job
      setSelectedJob(null); // Clear the selected job
      if (outputRef.current) {
        outputRef.current.clearOutput(); // Clear output state
      }
    } catch (err) {
      console.error("Error deleting job:", err);
      const detail = err?.response?.data?.detail || "Unknown error";
      alert(`Error deleting job: ${detail}`);
    }
  };

  // Define the function to handle running the job
  const handleRunJob = async () => {
    try {
      const response = await api.post(`/run-job`, { job_name: selectedJob.name });
      alert(response.data.message); // Display success message
      setShowModal(false); // Close modal after running the job
      refreshJobs(); // Refresh job status after running
    } catch (err) {
      console.error("Error running job:", err);
      if (err.response && err.response.data && err.response.data.detail) {
        alert(`Error running job: ${err.response.data.detail}`);
      } else {
        alert("Error running job");
      }
    }
  };

  const handlePauseJob = async () => {
    try {
      const response = await api.post(`/pause-job`, { job_name: selectedJob.name });
      alert(response.data.message); // Display success message
      setShowModal(false); // Close modal after pausing the job
      refreshJobs(); // Refresh job status after pausing
    } catch (err) {
      console.error("Error pausing job:", err);
      if (err.response && err.response.data && err.response.data.detail) {
        alert(`Error pausing job: ${err.response.data.detail}`);
      } else {
        alert("Error pausing job");
      }
    }
  };

  const palatteIcon = (iconClass, color, type, disabled = false) => {
    return (
      <div
        className="palatte-icon"
        onClick={() => !disabled && handleIconClick(type)}
        style={{
          opacity: disabled ? 0.4 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          pointerEvents: disabled ? "none" : "auto",
        }}
      >
        <a
          className="palatte-icon d-flex justify-content-center align-items-center"
          href="#"
          style={{ textDecoration: "none", paddingTop: "10px" }}
        >
          <span
            style={{ fontSize: "30px", color: disabled ? "#999" : color }}
            className={iconClass}
          ></span>
        </a>
        <hr />
      </div>
    );
  };

  const refreshJobDetails = async (jobName) => {
    try {
      const response = await api.get(`/job/${jobName}`);
      setSelectedJob(response.data.job);
    } catch (err) {
      console.error("Error refreshing job data:", err);
    }
  };

  const renderContent = () => {
    return (
      <>
        <div style={{ display: activeDiv === "Status" ? "block" : "none" }}>
          <Status job={selectedJob} refreshJobs={refreshJobs} onSelectJob={handleSelectJob} />
        </div>
        <div style={{ display: activeDiv === "Setup" ? "block" : "none" }}>
          <Setup
            selectedJob={selectedJob}
            refreshJobDetails={refreshJobDetails}
          />
        </div>
        <div style={{ display: activeDiv === "Namelists" ? "block" : "none" }}>
          <Namelists job={selectedJob} />
        </div>
        <div style={{ display: activeDiv === "Output" ? "block" : "none" }}>
          {selectedJob && (
            <Output
              ref={outputRef}
              job={selectedJob}
              jobOutputs={jobOutputs}
              setJobOutputs={setJobOutputs}
            />
          )}{" "}
        </div>
        <div style={{ display: activeDiv === "Plots" ? "block" : "none" }}>
          <Plots job={selectedJob} />
        </div>
      </>
    );
  };

  const renderModalContent = () => {
    switch (modalContent) {
      case "Add Job":
        return (
          <div>
            <div>
              <label>Name for New Job:</label>
              <input
                type="text"
                className="form-control"
                value={newJobName}
                onChange={(e) => setNewJobName(e.target.value)}
              />
            </div>
          </div>
        );
      case "Remove Job":
        return (
          <div>
            <label>Are you sure you want to remove the job?</label>
            <label>This action is irreversible!</label>
          </div>
        );
      case "Run Job":
        return (
          <div>
            <label>Are you sure you want to run the job?</label>
          </div>
        );
      default:
        return null;
    }
  };
  useEffect(() => {
    let intervalId;

    // Function to fetch and update job details
    const fetchJobDetails = async () => {
      try {
        const response = await api.get(`/job/${selectedJob.name}`);
        const updatedJob = response.data.job;

        // Check if the job status has changed
        if (updatedJob.status !== selectedJob.status) {
          setSelectedJob(updatedJob);
        }
      } catch (err) {
        console.error("Error updating job data:", err);
      }
    };

    if (selectedJob) {
      // Start polling every 5 seconds
      intervalId = setInterval(fetchJobDetails, 5000);
    }

    return () => {
      // Clear the interval when component unmounts or selectedJob changes
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [selectedJob]);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
        <button className="btn btn-outline-secondary btn-sm" onClick={onLogout}>
          Logout
        </button>
      </div>
      <div>
        <p className="top-text">Job</p>
        <div className="parent" style={{ position: "relative" }}>
          <div className="palatte-area">
            <FileStructure
              onSelectJob={handleSelectJob}
              setRefreshJobs={setRefreshJobs}
              selectedJobName={selectedJob?.name || null}
            />
            <button
              onClick={() => setShowBrowse(true)}
              style={{
                marginTop: "12px",
                padding: "6px 10px",
                backgroundColor: showBrowse ? "#2c5d8f" : "#4A90E2",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "bold",
                width: "90%",
              }}
            >
              🔭 Browse Published
            </button>
          </div>
          <div className="content-area">
            <div className="icons">
              {palleteIconClasses.map((iconClass, index) => {
                const type = types[index];
                const isRef = selectedJob && selectedJob.published_ref;
                const runAllowed =
                  selectedJob && !isRef &&
                  ["RUNNABLE", "PAUSED", "RUNNING"].includes(selectedJob.status);
                const pauseAllowed =
                  selectedJob && !isRef && selectedJob.status === "RUNNING";
                const isDisabled =
                  (type === "Remove Job" && !selectedJob) ||
                  (type === "Run Job" && !runAllowed) ||
                  (type === "Pause Job" && !pauseAllowed);
                return (
                  <div key={index}>
                    {palatteIcon(iconClass, colors[index], type, isDisabled)}
                  </div>
                );
              })}
            </div>
            <div className="task" style={{ position: "relative" }}>
              {showBrowse ? (
                <BrowsePublished
                  myEmail={myEmail}
                  refreshJobs={refreshJobs}
                  onSelectJob={handleSelectJob}
                  onClose={() => setShowBrowse(false)}
                />
              ) : (
                <>
                  <button onClick={() => handleButtonClick("Status")}>
                    Status
                  </button>
                  <button onClick={() => handleButtonClick("Setup")}>Setup</button>
                  <button onClick={() => handleButtonClick("Namelists")}>
                    Namelists
                  </button>
                  <button onClick={() => handleButtonClick("Output")}>
                    Output
                  </button>
                  <button onClick={() => handleButtonClick("Plots")}>Plots</button>
                  {renderContent()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{modalContent} Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderModalContent()}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              if (modalContent === "Add Job") {
                handleAddJob();
              } else if (modalContent === "Remove Job") {
                handleDeleteJob();
              } else if (modalContent === "Run Job") {
                handleRunJob(); // Trigger handleRunJob on clicking OK
              } else if (modalContent === "Pause Job") {
                handlePauseJob(); // Trigger handlePauseJob on clicking OK
              }
              handleCloseModal();
            }}
          >
            OK
          </Button>{" "}
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default HomePage;
